import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import { CustomerSignatureCard } from '../components/CustomerSignatureCard'
import { CustomerReferralPanel } from '../components/ReferralPanel'
import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { customersApi, supplyPointsApi, supplyPointTypesApi, type LegalRepresentative } from '../api/customers'
import type {
  Customer,
  SupplyPoint,
  SupplyPointFieldDef,
  SupplyPointRequest,
  SupplyPointType,
} from '../api/customers'
import { fetchMe, type LoginResponse } from '../api/auth'
import { contractsApi, CONTRACT_STATUS_LABELS, type Contract } from '../api/contracts'
import { domainsApi, type DomainList } from '../api/domains'
import { STAFF_ROLES } from '../navigation/roles'
import { DomainsTable } from './DomainsPage'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  EmptyState,
  IconButton,
  LoadingState,
  Page as PageShell,
  PageHeader,
  PaneLayout,
  CardGrid,
  KeyValueList,
  Select,
  StatusPill,
  TextField,
  useConfirm,
  type PillTone,
} from '../components/ui'

// The API returns additional fields for the detail view that aren't in the list interface
interface CustomerDetail extends Customer {
  address?: string
  postalCode?: string
  country?: string
  fiscalCode?: string
  vatNumber?: string
  pec?: string
  sdiCode?: string
  mobile?: string
  notes?: string
  birthDate?: string | null
  birthCity?: string | null
  birthProvince?: string | null
}

/**
 * TASK-0045: same Admin-tier check `CompanyDetailPage.tsx` (TASK-0035) established for gating the
 * merge panel — `auth.role === 'Administrator' || auth.role === 'EqualAdmin'`, the two role values
 * `PolicyNames.Admin` grants server-side. Reused here to gate the "Elimina" button on each supply
 * point row, mirroring `SupplyPointsController.Delete`'s `[Authorize(PolicyNames.Admin)]`.
 */
function isAdmin(auth: LoginResponse | null): boolean {
  return auth != null && (auth.role === 'Administrator' || auth.role === 'EqualAdmin')
}

/**
 * F-111 (REQ-006-009): tipi, etichette e campi di settore arrivano dal server (`/api/supply-point-types`);
 * qui non resta nessuna costante di settore. Il colore dipende solo dal gruppo del tipo.
 */
function typePillTone(type: SupplyPointType | undefined): PillTone {
  if (type?.group === 'energia') return 'warning'
  if (type?.group === 'telefonia') return 'primary'
  return 'neutral'
}

function findType(types: SupplyPointType[], code: string): SupplyPointType | undefined {
  return types.find(t => t.code === code)
}

/** Valore di un campo di settore come testo, o null se non c'e' nulla da mostrare. */
function fieldDisplay(sp: SupplyPoint, f: SupplyPointFieldDef): string | null {
  const v = (sp as unknown as Record<string, unknown>)[f.key]
  if (v == null || v === '') return null
  if (f.kind === 'boolean') return v === true ? f.label : null
  if (f.kind === 'select') return `${f.label}: ${f.options?.find(o => o.value === v)?.label ?? String(v)}`
  return `${f.label}: ${String(v)}`
}

interface SupplyPointFormState {
  supplyType: string
  code: string
  city: string
  province: string
  address: string
  isResident: boolean
  powerKw: string
  annualConsumption: string
  meterType: string
  meterSerial: string
  usageType: string
  isVulnerable: boolean
  lineType: string
  isMigration: boolean
  migrationCode: string
  notes: string
}

const emptySupplyPointForm: SupplyPointFormState = {
  supplyType: '',
  code: '',
  city: '',
  province: '',
  address: '',
  isResident: false,
  powerKw: '',
  annualConsumption: '',
  meterType: 'monorario',
  meterSerial: '',
  usageType: 'domestic',
  isVulnerable: false,
  lineType: '',
  isMigration: false,
  migrationCode: '',
  notes: '',
}

function supplyPointToForm(sp: SupplyPoint): SupplyPointFormState {
  return {
    supplyType: sp.supplyType,
    code: sp.code ?? '',
    city: sp.city ?? '',
    province: sp.province ?? '',
    address: sp.address ?? '',
    isResident: sp.isResident,
    powerKw: sp.powerKw != null ? String(sp.powerKw) : '',
    annualConsumption: sp.annualConsumption != null ? String(sp.annualConsumption) : '',
    meterType: sp.meterType || 'monorario',
    meterSerial: sp.meterSerial ?? '',
    usageType: sp.usageType || 'domestic',
    isVulnerable: sp.isVulnerable,
    lineType: sp.lineType ?? '',
    isMigration: sp.isMigration,
    migrationCode: sp.migrationCode ?? '',
    notes: sp.notes ?? '',
  }
}

function formToSupplyPointRequest(customerId: number, f: SupplyPointFormState): SupplyPointRequest {
  return {
    supplyType: f.supplyType,
    customerId,
    code: f.code.trim() || undefined,
    city: f.city.trim() || undefined,
    province: f.province.trim() || undefined,
    address: f.address.trim() || undefined,
    notes: f.notes.trim() || undefined,
    isResident: f.isResident,
    powerKw: f.powerKw !== '' ? Number(f.powerKw) : undefined,
    annualConsumption: f.annualConsumption !== '' ? Number(f.annualConsumption) : undefined,
    meterType: f.meterType || undefined,
    meterSerial: f.meterSerial.trim() || undefined,
    usageType: f.usageType || undefined,
    isVulnerable: f.isVulnerable,
    lineType: f.lineType.trim() || undefined,
    isMigration: f.isMigration,
    migrationCode: f.migrationCode.trim() || undefined,
  }
}

/** Inline create/edit form for one supply point. F-111: il tipo e i campi di settore arrivano dal server;
 * i campi comuni (codice, citta', provincia, indirizzo, note) sono sempre presenti. */
function SupplyPointForm({
  initial,
  types,
  saving,
  onSave,
  onCancel,
}: {
  initial: SupplyPointFormState
  types: SupplyPointType[]
  saving: boolean
  onSave: (f: SupplyPointFormState) => void
  onCancel: () => void
}) {
  const active = types.filter(t => t.isActive)
  const [f, setF] = useState<SupplyPointFormState>({
    ...initial,
    supplyType: initial.supplyType || active[0]?.code || '',
  })
  const current = findType(types, f.supplyType)
  // Un punto che ha gia' un tipo ritirato lo conserva nell'elenco, senza offrirlo agli altri.
  const options = current && !current.isActive ? [current, ...active] : active

  function setField(key: string, value: string | boolean) {
    setF(prev => ({ ...prev, [key]: value }))
  }

  function renderField(def: SupplyPointFieldDef) {
    const state = f as unknown as Record<string, string | boolean>
    const id = `sp-${def.key}`
    if (def.kind === 'boolean') {
      return (
        <div key={def.key} className="d-flex align-items-end">
          <Checkbox
            id={id}
            label={def.label}
            checked={state[def.key] === true}
            onChange={e => setField(def.key, e.target.checked)}
            disabled={saving}
          />
        </div>
      )
    }
    return (
      <div key={def.key}>
        {def.kind === 'select' ? (
          <Select
            id={id}
            label={def.label}
            value={String(state[def.key] ?? '')}
            onChange={e => setField(def.key, e.target.value)}
            disabled={saving}
          >
            {(def.options ?? []).map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        ) : (
          <TextField
            id={id}
            label={def.label}
            type={def.kind === 'decimal' || def.kind === 'integer' ? 'number' : 'text'}
            step={def.kind === 'decimal' ? '0.01' : undefined}
            value={String(state[def.key] ?? '')}
            onChange={e => setField(def.key, e.target.value)}
            disabled={saving}
            maxLength={def.kind === 'text' ? 50 : undefined}
          />
        )}
      </div>
    )
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        onSave(f)
      }}
    >
      <Card as="div" variant="filled" padding="md">
      <FormGrid columns={4}>
        <div>
          <Select
            label="Tipo"
            value={f.supplyType}
            onChange={e => setF({ ...f, supplyType: e.target.value })}
            disabled={saving || options.length === 0}
          >
            {f.supplyType && !options.some(t => t.code === f.supplyType) && (
              <option value={f.supplyType}>{f.supplyType}</option>
            )}
            {options.map(t => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <TextField
            label="Codice"
            value={f.code}
            onChange={e => setF({ ...f, code: e.target.value })}
            disabled={saving}
            maxLength={50}
          />
        </div>
        <div>
          <TextField
            label="Città"
            value={f.city}
            onChange={e => setF({ ...f, city: e.target.value })}
            disabled={saving}
            maxLength={100}
          />
        </div>
        <div>
          <TextField
            label="Provincia"
            value={f.province}
            onChange={e => setF({ ...f, province: e.target.value })}
            disabled={saving}
            maxLength={10}
          />
        </div>
        <div className="md-span-2">
          <TextField
            label="Indirizzo"
            value={f.address}
            onChange={e => setF({ ...f, address: e.target.value })}
            disabled={saving}
            placeholder="Indirizzo del punto di fornitura, se diverso dal cliente"
          />
        </div>

        {(current?.fields ?? []).map(renderField)}

        <div className="md-span-full">
          <TextField
            label="Note"
            multiline
            rows={2}
            value={f.notes}
            onChange={e => setF({ ...f, notes: e.target.value })}
            disabled={saving}
          />
        </div>
      </FormGrid>

      <div className="d-flex gap-2 mt-3">
        <Button type="submit" variant="filled" size="sm" disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <Button type="button" variant="text" size="sm" disabled={saving} onClick={onCancel}>
          Annulla
        </Button>
      </div>
      </Card>
    </form>
  )
}

/**
 * Pratiche del cliente. Una pratica ricaricata dopo un KO resta una sola riga con l'ultimo codice
 * portale; i codici assegnati prima sono elencati sotto (2026-10-16).
 */
function CustomerContractsSection({ customerId }: { customerId: number }) {
  const [items, setItems] = useState<Contract[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    contractsApi.list({ customerId, size: 200 })
      .then(p => { if (alive) setItems(p.items) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore nel caricamento') })
    return () => { alive = false }
  }, [customerId])
  return (
    <Card variant="outlined">
      <CardHeader icon="description" title="Contratti" />
      <CardContent>
        {error ? <Callout tone="error">{error}</Callout>
          : items === null ? <LoadingState />
          : items.length === 0 ? <EmptyState icon="description" title="Nessun contratto associato" compact />
          : (
            <ul className="flex flex-col gap-2">
              {items.map(c => (
                <li key={c.id}>
                  <Link to={`/contracts/${c.id}`}><strong>{c.referenceCode}</strong></Link>
                  {' · '}{CONTRACT_STATUS_LABELS[c.status] ?? c.status}
                  {c.previousCodes && c.previousCodes.length > 0 && (
                    <div className="md-typescale-body-small text-muted">
                      Codici precedenti (KO): {c.previousCodes.join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  )
}


/**
 * Domini del cliente, con la stessa evidenza delle scadenze della pagina Domini. Solo per lo staff Equal: l'API dei
 * domini non è degli agenti (risponderebbe 403).
 */
function CustomerDomainsSection({ customerId }: { customerId: number }) {
  const [data, setData] = useState<DomainList | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    domainsApi.list({ customerId, pageSize: 200 })
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore nel caricamento') })
    return () => { alive = false }
  }, [customerId])
  return (
    <Card variant="outlined">
      <CardHeader
        icon="language"
        title="Domini"
        actions={<ButtonLink to={`/domains/new?customerId=${customerId}`} variant="text" icon="add">Aggiungi</ButtonLink>}
      />
      <CardContent>
        {error ? <Callout tone="error">{error}</Callout>
          : data === null ? <LoadingState />
          : data.items.length === 0 ? <EmptyState icon="language" title="Nessun dominio associato" compact />
          : <DomainsTable items={data.items} today={data.today} />}
      </CardContent>
    </Card>
  )
}

/** "Punti di Fornitura" section — same principle as `ContractDetailPage.tsx`'s Notes section
 * (TASK-0025): a list of the current customer's supply points plus an inline create form, with
 * per-row inline edit. Delete is Admin-gated client-side (the backend already enforces
 * `[Authorize(PolicyNames.Admin)]` on `DELETE /api/supply-points/{id}` — this hides the action
 * from non-Admins rather than relying on the 403 to teach them it exists). */
function SupplyPointsSection({
  customerId,
  auth,
  points,
  onChanged,
}: {
  customerId: number
  auth: LoginResponse | null
  points: SupplyPoint[]
  onChanged: () => void
}) {
  const confirm = useConfirm()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // F-111: tipi (con etichette e campi) dal server. Un punto con un tipo ritirato lo trova comunque
  // nell'elenco dell'Admin; per gli altri il tipo resta visibile come codice.
  const [types, setTypes] = useState<SupplyPointType[]>([])
  useEffect(() => {
    let alive = true
    supplyPointTypesApi
      .list(isAdmin(auth))
      .then(r => { if (alive) setTypes(r.items) })
      .catch(() => { if (alive) setTypes([]) })
    return () => { alive = false }
  }, [auth])

  async function handleCreate(f: SupplyPointFormState) {
    setSaving(true)
    setError(null)
    try {
      await supplyPointsApi.create(formToSupplyPointRequest(customerId, f))
      setAdding(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nella creazione del punto di fornitura.')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdate(id: number, f: SupplyPointFormState) {
    setSaving(true)
    setError(null)
    try {
      await supplyPointsApi.update(id, formToSupplyPointRequest(customerId, f))
      setEditingId(null)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio del punto di fornitura.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: number) {
    const ok = await confirm({
      title: 'Eliminare il punto di fornitura',
      message: 'Eliminare questo punto di fornitura?',
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    setSaving(true)
    setError(null)
    try {
      await supplyPointsApi.remove(id)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore nell'eliminazione del punto di fornitura.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader
        icon="bolt"
        title={
          <>
            Punti di Fornitura <StatusPill tone="neutral">{points.length}</StatusPill>
          </>
        }
        actions={
          !adding && (
            <Button
              variant="outlined"
              size="sm"
              icon="add"
              onClick={() => {
                setAdding(true)
                setEditingId(null)
              }}
            >
              Aggiungi
            </Button>
          )
        }
      />

      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}

        {points.length === 0 && !adding && (
          <EmptyState icon="bolt" title="Nessun punto di fornitura associato" compact />
        )}

        <div className="d-flex flex-column gap-2">
          {points.map(sp =>
            editingId === sp.id ? (
              <SupplyPointForm
                key={sp.id}
                initial={supplyPointToForm(sp)}
                types={types}
                saving={saving}
                onSave={f => handleUpdate(sp.id, f)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <Card as="div" variant="filled" padding="sm" key={sp.id}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <StatusPill tone={typePillTone(findType(types, sp.supplyType))} className="me-2">
                      {findType(types, sp.supplyType)?.label ?? sp.supplyType}
                    </StatusPill>
                    <strong>{sp.code || `#${sp.id}`}</strong>
                    {(sp.city || sp.province) && (
                      <span className="text-muted md-typescale-body-small ms-2">
                        {[sp.city, sp.province].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-1">
                    <IconButton
                      icon="edit"
                      size="sm"
                      aria-label="Modifica"
                      disabled={saving}
                      onClick={() => {
                        setEditingId(sp.id)
                        setAdding(false)
                      }}
                    />
                    {isAdmin(auth) && (
                      <IconButton
                        icon="delete"
                        size="sm"
                        aria-label="Elimina"
                        disabled={saving}
                        onClick={() => handleDelete(sp.id)}
                      />
                    )}
                  </div>
                </div>
                <div className="md-typescale-body-small text-muted mt-1">
                  {(findType(types, sp.supplyType)?.fields ?? [])
                    .map(f => fieldDisplay(sp, f))
                    .filter((x): x is string => x != null)
                    .join(' · ')}
                </div>
                {sp.address && <div className="md-typescale-body-small text-muted">Indirizzo: {sp.address}</div>}
                {sp.notes && <div className="md-typescale-body-small mt-1">{sp.notes}</div>}
              </Card>
            ),
          )}
        </div>

        {adding && (
          <div className="mt-2">
            <SupplyPointForm
              initial={emptySupplyPointForm}
              types={types}
              saving={saving}
              onSave={handleCreate}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function displayName(c: CustomerDetail): string {
  if (c.companyName) return c.companyName
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Rappresentante legale (aziende e condomini): prima si vedeva solo aprendo Modifica. */
function LegalRepresentativeCard({ customerId }: { customerId: number }) {
  const [rep, setRep] = useState<LegalRepresentative | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    customersApi.getLegalRepresentative(customerId)
      .then(r => { if (alive) setRep(r.representative) })
      .catch(() => { if (alive) setRep(null) })
    return () => { alive = false }
  }, [customerId])
  if (rep === undefined) return null
  const birth = rep && (rep.birthCity || rep.birthDate)
    ? [rep.birthCity ? (rep.birthProvince ? `${rep.birthCity} (${rep.birthProvince})` : rep.birthCity) : null,
       rep.birthDate ? new Date(rep.birthDate).toLocaleDateString('it-IT') : null].filter(Boolean).join(', ')
    : null
  return (
    <Card variant="outlined">
      <CardHeader icon="badge" title={rep?.role || 'Rappresentante legale'}
        actions={<Link to={`/customers/${customerId}/edit`} className="md-typescale-label-large">Modifica</Link>} />
      <CardContent>
        {rep ? (
          <KeyValueList columns={2}
            items={[
              { label: 'Nome', value: <>{[rep.firstName, rep.lastName].filter(Boolean).join(' ') || '—'}</> },
              { label: 'Codice fiscale', value: <>{rep.fiscalCode || '—'}</> },
              ...(birth ? [{ label: 'Nato/a', value: <>{birth}</> }] : []),
            ]}
          />
        ) : (
          <span className="text-muted md-typescale-body-small">Non inserito. Aggiungilo con Modifica.</span>
        )}
      </CardContent>
    </Card>
  )
}

function customerTypeBadge(type: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    persona_fisica: { tone: 'info', label: 'Persona Fisica' },
    azienda: { tone: 'primary', label: 'Azienda' },
    professionista: { tone: 'neutral', label: 'Professionista' },
    person: { tone: 'info', label: 'Persona' },
    company: { tone: 'primary', label: 'Azienda' },
  }
  const entry = map[type] ?? { tone: 'neutral' as PillTone, label: type }
  return <StatusPill tone={entry.tone} className="me-2">{entry.label}</StatusPill>
}

function statusBadge(status: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    active: { tone: 'success', label: 'Attivo' },
    inactive: { tone: 'warning', label: 'Inattivo' },
    suspended: { tone: 'error', label: 'Sospeso' },
  }
  const entry = map[status] ?? {
    tone: 'neutral' as PillTone,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

export function CustomerDetailPage() {
  const { id: idStr } = useParams<{ id: string }>()
  const id = parseInt(idStr ?? '', 10)

  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [supplyPoints, setSupplyPoints] = useState<SupplyPoint[]>([])
  const [revealKey, setRevealKey] = useState(0) // C-08: rilettura dopo la visione motivata

  // TASK-0045: this page (unlike Dashboard/CompanyDetailPage/ContestDetailPage) is routed without
  // an `auth` prop today — `App.tsx` is out of this task's allowed_paths, so rather than prop-drill
  // through a file this task must not touch, the current session is fetched directly the same way
  // `App.tsx` itself does on startup (`fetchMe`, TASK-0044). Used only to gate the Admin-only
  // "Elimina" button on each supply point row.
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  useEffect(() => {
    fetchMe().then(setAuth).catch(() => setAuth(null))
  }, [])

  function loadSupplyPoints(customerId: number) {
    supplyPointsApi
      .list(customerId)
      .then(page => setSupplyPoints(page.items))
      .catch(() => setSupplyPoints([]))
  }

  useEffect(() => {
    if (isNaN(id)) {
      setError('ID cliente non valido.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setNotFound(false)
    customersApi
      .get(id)
      .then(data => {
        setCustomer(data as CustomerDetail)
        loadSupplyPoints(id)
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true)
        } else {
          setError(msg || 'Errore nel caricamento del cliente.')
        }
      })
      .finally(() => setLoading(false))
  }, [id, revealKey])

  if (loading) {
    return <LoadingState />
  }

  if (notFound) {
    return (
      <Callout tone="warning">
        Cliente non trovato.{' '}
        <Link to="/customers">Torna alla lista</Link>
      </Callout>
    )
  }

  if (error) {
    return <Callout tone="error">{error}</Callout>
  }

  if (!customer) return null

  const c = customer

  return (
    <PageShell>
      <PageHeader
        back={{ to: '/customers', label: 'Clienti' }}
        icon="person"
        title={displayName(c)}
        subtitle={
          <>
            {customerTypeBadge(c.customerType)}
            {statusBadge(c.status)}
          </>
        }
        actions={
          <RevealSensitiveButton entityType="customers" entityId={c.id} values={[c.phone, c.mobile, c.fiscalCode]} onRevealed={() => setRevealKey(k => k + 1)} />
        }
      />

      {/* Body */}
      <PaneLayout
        variant="supporting"
        main={
          <>
            {/* Left: anagrafica */}
            <Card variant="outlined">
              <CardHeader icon="person" title="Anagrafica" />
              <CardContent>
                <KeyValueList columns={2}
                  items={[
                    {
                      label: 'Nome',
                      value: (
                        <>
                          {c.firstName || c.lastName
                            ? [c.firstName, c.lastName].filter(Boolean).join(' ')
                            : '—'}
                        </>
                      ),
                    },
                    { label: 'Azienda', value: <>{c.companyName || '—'}</> },
                    {
                      label: 'Email',
                      value: (
                        <>
                          {c.email ? (
                            <a href={`mailto:${c.email}`}>{c.email}</a>
                          ) : (
                            '—'
                          )}
                        </>
                      ),
                    },
                    {
                      label: 'Telefono',
                      value: (
                        <>
                          {c.phone ? (
                            <a href={`tel:${c.phone}`}>{c.phone}</a>
                          ) : (
                            '—'
                          )}
                        </>
                      ),
                    },
                    { label: 'Mobile', value: <>{c.mobile || '—'}</> },
                    {
                      label: 'Città',
                      value: (
                        <>
                          {c.city
                            ? c.province
                              ? `${c.city} (${c.province})`
                              : c.city
                            : '—'}
                        </>
                      ),
                    },
                    { label: 'Indirizzo', value: <>{c.address || '—'}</>, full: true },
                    { label: 'Codice fiscale', value: <>{c.fiscalCode || '—'}</> },
                    ...(c.birthDate || c.birthCity
                      ? [{
                          label: 'Nato/a',
                          value: <>{[c.birthCity ? (c.birthProvince ? `${c.birthCity} (${c.birthProvince})` : c.birthCity) : null, c.birthDate ? new Date(c.birthDate).toLocaleDateString('it-IT') : null].filter(Boolean).join(', ')}</>,
                        }]
                      : []),
                    { label: 'P.IVA', value: <>{c.vatNumber || '—'}</> },
                    { label: 'PEC', value: <>{c.pec || '—'}</> },
                    { label: 'Note', value: <><span className="text-muted md-typescale-body-small">{c.notes || '—'}</span></>, full: true },
                  ]}
                />
              </CardContent>
            </Card>
            {(c.customerType === 'company' || c.customerType === 'condominium') && <LegalRepresentativeCard customerId={c.id} />}
            {/* Firma e timbro salvati del cliente: finiscono nei moduli generati (anche per gli agenti sui propri clienti). */}
            {auth && STAFF_ROLES.includes(auth.role) && <CustomerDomainsSection customerId={c.id} />}
            <CustomerSignatureCard customerId={c.id} required={false} />
            {/* Porta un amico (Ermes): amici portati e foglio del segnalatore, come nella scheda del contatto collegato. */}
            {auth && <CustomerReferralPanel customerId={c.id} auth={auth} />}
          </>
        }
        pane={
          <>
            {/* Right: riepilogo + contratti */}
            <Card variant="outlined">
              <CardContent>
                <CardGrid min={200} gap="collection" className="text-center">
                  <Card as="div" variant="filled" padding="sm" className="gap-1">
                    <div className="md-typescale-body-small text-muted">Codice</div>
                    <strong>{c.customerCode}</strong>
                  </Card>
                  <Card as="div" variant="filled" padding="sm" className="gap-1">
                    <div className="md-typescale-body-small text-muted">Registrato il</div>
                    <strong>{formatDate(c.createdAt)}</strong>
                  </Card>
                </CardGrid>
              </CardContent>
            </Card>

            <CustomerContractsSection customerId={id} />

            <SupplyPointsSection
              customerId={id}
              auth={auth}
              points={supplyPoints}
              onChanged={() => loadSupplyPoints(id)}
            />
          </>
        }
      />
    </PageShell>
  )
}
